import { useCallback, useMemo } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { yakurekiPollKey } from '@/apps/latest/shared/recording-status/usePollEvent'
import { updateSOAP } from '@/shared/apis/yakureki'
import { queryKeys } from '@/shared/hooks/queries/queryKeys'
import { type YakurekiDetail } from '@/shared/types/yakurekiCore'
import { type Soaps } from '@/shared/types/yakurekiSoap'

// latest の SOAP 書き込みの唯一の入口 (行編集 / 採否トグル / 定型文挿入 / 一括置換)。
//
// ── なぜ cache 起点なのか (log/186) ──────────────────────────────────────
// SOAP 更新は 6 section を丸ごと送る全置換 API (`PATCH /api/yakureki/<id>/soaps/`)。
// back は new_data の SOAP_SECTION_FIELDS だけを読み、sidecar は旧 version から引き継ぐ
// (yakureki/crud.py update_soap_with_history)。つまり **送った 6 section が全て**。
//
// 送信 body を render 時点の閉包 (props の event.soaps) から組むと、往復中に別の section を
// 保存した 2 本目が 1 本目の変更を含まない body を送り、**先に保存した section が黙って消える**。
// 保存は成功扱いなので気づく手掛かりが無い。訪問報告書 (useHomeVisitReportEditing) と同じ
// 欠陥で、あちらと同じ 3 点セットで塞ぐ:
//   1. body も items 解決も cache (getQueryData) の最新から組む (= 閉包を正典にしない)
//   2. onMutate で楽観更新 + onError でロールバック
//      (楽観更新が無いと採否が往復ぶん無反応に見え、再クリックを誘発して 1. の窓に入る)
//   3. scope で同一 event の更新を直列化する
//
// scope が待たせるのは mutationFn だけで onMutate は即時に走る (query-core の
// Mutation.execute は onMutate を await してから retryer.start() する) ため、
// 楽観更新の即時性は保たれる。queue 待ちの間に積み上がった編集は、実行時に cache を
// 読み直す mutationFn がまとめて送る。
//
// なお apps/legacy は楽観更新版 (useToggleSelectionMutation) と非楽観版
// (useUpdateSoapMutation) を併用しており、そちらは本 hook の対象外。

// 現在の soaps (cache の最新) を受けて、次に保存する soaps を返すレシピ。
// null を返すと保存しない (= 対象行が無い等)。
export type SoapRecipe = (current: Soaps) => Omit<Soaps, 'id'> | null

export const useSoapWrite = ({ eventId }: { eventId: string }) => {
  const queryClient = useQueryClient()
  const detailKey = useMemo(() => yakurekiPollKey(eventId), [eventId])

  const { mutateAsync } = useMutation({
    // 同一 event の SOAP 更新は直列実行する (TanStack Query v5 の mutation scope)。
    scope: { id: `soap:${eventId}` },

    mutationFn: async ({
      editSource,
    }: {
      next: Omit<Soaps, 'id'>
      editSource?: string
    }) => {
      // 楽観更新済みの cache をそのまま送る (onMutate で反映済みなのでここで再適用しない)。
      const detail = queryClient.getQueryData<YakurekiDetail>(detailKey)
      if (!detail?.soaps) throw new Error('soaps not found')
      const { id: _id, ...rest } = detail.soaps
      await updateSOAP(eventId, rest, editSource)
    },

    onMutate: async ({
      next,
    }: {
      next: Omit<Soaps, 'id'>
      editSource?: string
    }) => {
      await queryClient.cancelQueries({ queryKey: detailKey })
      const previous = queryClient.getQueryData<YakurekiDetail>(detailKey)
      queryClient.setQueryData<YakurekiDetail>(detailKey, (old) =>
        old?.soaps ? { ...old, soaps: { ...old.soaps, ...next } } : old
      )
      return { previous }
    },

    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(detailKey, context.previous)
      }
    },

    // 保存後はサーバ値へ収束させる。back は content 差分があれば新 version を作るため、
    // version / 編集履歴を持つ画面が古いままにならないようにする。
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: detailKey })
      void queryClient.invalidateQueries({
        queryKey: queryKeys.soapEditHistory.list(eventId),
      })
    },
  })

  // cache の最新 soaps を読む (レンダ props ではなくこちらを正典にする)。
  const currentSoaps = useCallback(
    (): Soaps | null =>
      queryClient.getQueryData<YakurekiDetail>(detailKey)?.soaps ?? null,
    [queryClient, detailKey]
  )

  // recipe を cache の最新に当てて保存する。成功で true。
  // false = 保存しなかった (soaps 未着 / recipe が null) か、保存に失敗した。
  // 通知は呼び出し側の文脈 (挿入 / 保存 / 置換) で出し分けるためここでは出さない。
  const write = useCallback(
    async (recipe: SoapRecipe, editSource?: string): Promise<boolean> => {
      const current = currentSoaps()
      if (!current) return false
      const next = recipe(current)
      if (!next) return false
      try {
        await mutateAsync({ next, editSource })
        return true
      } catch {
        return false
      }
    },
    [currentSoaps, mutateAsync]
  )

  return { write, currentSoaps }
}
